<template>
  <b-card>
    <b-card-title class="mb-1 text-center">
      {{ $t('risk_indicator') }}
    </b-card-title>

    <!-- Gauge Chart -->
    <vue-apex-charts
      type="radialBar"
      height="280"
      :options="chartOptions"
      :series="[gaugeValue]"
    />

    <!-- Risk Zone Labels -->
    <div class="d-flex justify-content-around mt-2 mb-2">
      <div class="text-center">
        <b-card-text class="mb-0">
          <span class="dot-success">&#x25CF;</span> {{ $t('risk_zone_satisfactory') }}
        </b-card-text>
        <b-card-text class="mb-0 text-muted">
          (0-33%)
        </b-card-text>
      </div>
      <div class="text-center">
        <b-card-text class="mb-0">
          <span class="dot-warning">&#x25CF;</span> {{ $t('risk_zone_deficient') }}
        </b-card-text>
        <b-card-text class="mb-0 text-muted">
          (34-67%)
        </b-card-text>
      </div>
      <div class="text-center">
        <b-card-text class="mb-0">
          <span class="dot-danger">&#x25CF;</span> {{ $t('risk_zone_critical') }}
        </b-card-text>
        <b-card-text class="mb-0 text-muted">
          (68-100%)
        </b-card-text>
      </div>
    </div>

    <!-- Trend Information -->
    <div
      v-if="trendDirection"
      class="text-center mt-2"
    >
      <b-card-text class="mb-0 font-medium">
        <feather-icon
          :icon="trendDirection === 'up' ? 'TrendingUpIcon' : trendDirection === 'down' ? 'TrendingDownIcon' : 'MinusIcon'"
          size="18"
          :class="trendDirection === 'up' ? 'text-danger' : trendDirection === 'down' ? 'text-success' : 'text-warning'"
        />
        <span :class="trendDirection === 'up' ? 'text-danger' : trendDirection === 'down' ? 'text-success' : 'text-warning'">
          {{ getTrendMessage() }}
        </span>
      </b-card-text>
    </div>
  </b-card>
</template>

<script>
import { BCard, BCardTitle, BCardText } from 'bootstrap-vue'
import VueApexCharts from 'vue-apexcharts'
import i18n from '@/libs/i18n'

export default {
  components: {
    VueApexCharts,
    BCard,
    BCardTitle,
    BCardText,
  },
  props: {
    gaugeValue: {
      type: Number,
      required: true,
      default: 0,
    },
    trendDirection: {
      type: String,
      default: null,
      validator: value => ['up', 'down', 'stable', null].includes(value),
    },
    trendPercentage: {
      type: Number,
      default: 0,
    },
  },
  computed: {
    chartOptions() {
      return {
        plotOptions: {
          radialBar: {
            startAngle: -90,
            endAngle: 90,
            hollow: {
              size: '60%',
            },
            track: {
              background: '#e7e7e7',
              strokeWidth: '100%',
            },
            dataLabels: {
              name: {
                show: true,
                offsetY: -10,
                color: '#5e5873',
                fontSize: '0.857rem',
              },
              value: {
                offsetY: 5,
                color: '#5e5873',
                fontSize: '1.714rem',
                fontWeight: '600',
                formatter: val => `${Math.round(val)}%`,
              },
            },
          },
        },
        colors: [this.getGaugeColor()],
        fill: {
          type: 'gradient',
          gradient: {
            shade: 'dark',
            type: 'horizontal',
            shadeIntensity: 0.5,
            gradientToColors: [this.getGaugeGradientColor()],
            inverseColors: false,
            opacityFrom: 1,
            opacityTo: 1,
            stops: [0, 100],
          },
        },
        stroke: {
          lineCap: 'round',
        },
        labels: [i18n.t('risk_level')],
      }
    },
  },
  methods: {
    getGaugeColor() {
      // Green zone (low risk): 0-33%
      if (this.gaugeValue <= 33) return '#28a745'
      // Yellow zone (medium risk): 34-67%
      if (this.gaugeValue <= 67) return '#ffc107'
      // Red zone (high risk): 68-100%
      return '#dc3545'
    },
    getGaugeGradientColor() {
      // Gradient end colors for smooth transitions
      if (this.gaugeValue <= 33) return '#20c997'
      if (this.gaugeValue <= 67) return '#fd7e14'
      return '#e83e8c'
    },
    getTrendMessage() {
      const percentage = Math.abs(this.trendPercentage)
      if (this.trendDirection === 'up') {
        return `${i18n.t('risk_trend_increasing')} ${percentage}%`
      } if (this.trendDirection === 'down') {
        return `${i18n.t('risk_trend_decreasing')} ${percentage}%`
      }
      return i18n.t('risk_trend_stable')
    },
  },
}
</script>

<style scoped>
.font-medium {
  font-size: 16px;
  font-weight: 800;
}
.dot-success {
  color: #28a745;
  font-size: 20px;
}
.dot-warning {
  color: #ffc107;
  font-size: 20px;
}
.dot-danger {
  color: #dc3545;
  font-size: 20px;
}
</style>
